import { Component } from '@theme/component';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * Cart drawer component that displays cart contents in a slide-out panel.
 * Extends Component for event binding and ref management.
 * Cart item interactions (quantity, remove) are handled by nested cart-items-component.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element
 *
 * @extends {Component<Refs>}
 */
class CartDrawerComponent extends Component {
  requiredRefs = ['dialog'];

  #previousScrollY = 0;
  #pendingRenderTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    if (this.#pendingRenderTimeout) {
      clearTimeout(this.#pendingRenderTimeout);
    }
  }

  /**
   * Shows the cart drawer dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();

      this.#updateAriaExpanded(true);
      this.addEventListener('click', this.#handleClick);
      dialog.addEventListener('cancel', this.#handleCancel);
    });
  }

  /**
   * Closes the cart drawer dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    try {
      this.removeEventListener('click', this.#handleClick);
      dialog.removeEventListener('cancel', this.#handleCancel);

      dialog.classList.add('dialog-closing');

      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });

      dialog.close();
      dialog.classList.remove('dialog-closing');

      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
      this.#updateAriaExpanded(false);
    } catch (error) {
      // Ensure cleanup happens even if animation fails
      this.removeEventListener('click', this.#handleClick);
      dialog.removeEventListener('cancel', this.#handleCancel);
      dialog.classList.remove('dialog-closing');
      this.#updateAriaExpanded(false);
      throw error;
    }
  };

  /**
   * Toggles the cart drawer dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Updates aria-expanded state on the trigger button.
   * @param {boolean} expanded - Whether the dialog is expanded
   */
  #updateAriaExpanded(expanded) {
    const trigger = document.querySelector('[aria-controls="cart-drawer"]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', String(expanded));
    }
  }

  /**
   * Handles click events to close dialog when clicking outside.
   * @param {MouseEvent} event - The mouse event
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Handles dialog cancel event (ESC key).
   * @param {Event} event - The cancel event
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Handles cart update events to refresh the drawer.
   * Opens the drawer when a product is added to cart (CartAddEvent).
   * The nested cart-items-component handles morphing when sections data is available.
   * This handler only needs to fetch/render when sections data is NOT available.
   * Debounced to prevent race conditions on rapid cart updates.
   * @param {CustomEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    const isAddToCart = event instanceof CartAddEvent && !event.detail.data?.didError;

    // If sections data contains our section, cart-items-component will morph it
    // The morph updates the entire section including the count display
    const hasSectionData = !!event.detail.data.sections?.[this.sectionId];

    if (!hasSectionData) {
      // Debounce section rendering to prevent race conditions
      if (this.#pendingRenderTimeout) {
        clearTimeout(this.#pendingRenderTimeout);
      }

      // Section not in response (e.g., event from a source that didn't request our section)
      // We need to fetch and render the section ourselves
      this.#pendingRenderTimeout = setTimeout(() => {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
        this.#pendingRenderTimeout = null;
      }, 100);
    }

    if (isAddToCart) {
      this.showDialog();
    }
  };

  /**
   * Gets the section ID from the data attribute.
   * @returns {string} The section ID
   */
  get sectionId() {
    return this.dataset.sectionId || 'cart-drawer';
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
